export const SEO_SITE_NAME = 'Verde Confirma';
export const SEO_DEFAULT_DESCRIPTION =
  'Consulte propostas eleitorais, compare candidatos e explore o Match Político para entender posições políticas com rapidez.';
export const SEO_DEFAULT_IMAGE = '/hero.png';
export const SEO_INDEX_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
export const SEO_NOINDEX_ROBOTS = 'noindex,follow';

export type JsonLdValue = Record<string, unknown> | ReadonlyArray<Record<string, unknown>>;

export function buildPageTitle(pageTitle: string): string {
  const trimmedTitle = pageTitle.trim();

  return trimmedTitle.length > 0 ? `${trimmedTitle} | ${SEO_SITE_NAME}` : SEO_SITE_NAME;
}

export function buildCandidateProposalDescription(candidateName: string, candidateParty?: string): string {
  const resolvedParty = candidateParty?.trim();

  return `Leia o documento oficial de ${candidateName}${resolvedParty ? ` (${resolvedParty})` : ''} com navegação por página e fontes do TSE.`;
}

export function buildCandidateTopicProposalDescription(
  candidateName: string,
  topicLabel: string,
  candidateParty?: string,
): string {
  const resolvedParty = candidateParty?.trim();

  return `Leia o documento oficial de ${candidateName}${resolvedParty ? ` (${resolvedParty})` : ''} sobre ${topicLabel} com navegação por página e fontes do TSE.`;
}

export type CandidateProposalJsonLdInput = {
  candidateName: string;
  candidateParty?: string;
  canonicalPath: string;
  description: string;
  image?: string;
  topicLabel?: string;
};

export function buildCandidateProposalJsonLd(input: CandidateProposalJsonLdInput, origin = getSiteOrigin()): JsonLdValue {
  const canonicalUrl = buildAbsoluteUrl(input.canonicalPath, origin);
  const siteUrl = buildAbsoluteUrl('/', origin);
  const resolvedParty = input.candidateParty?.trim();
  const about: Record<string, unknown> = {
    '@type': 'Person',
    name: input.candidateName,
  };

  if (resolvedParty) {
    about.affiliation = {
      '@type': 'Organization',
      name: resolvedParty,
    };
  }

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: input.topicLabel ? `${input.candidateName} | ${input.topicLabel} | Proposta oficial` : `${input.candidateName} | Proposta oficial`,
    description: input.description,
    url: canonicalUrl,
    inLanguage: 'pt-BR',
    isPartOf: {
      '@type': 'WebSite',
      name: SEO_SITE_NAME,
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: SEO_SITE_NAME,
      url: siteUrl,
    },
    about,
  };

  if (input.image) {
    jsonLd.image = buildAbsoluteUrl(input.image, origin);
  }

  return jsonLd;
}

export function getSiteOrigin(): string {
  const configuredOrigin = import.meta.env.VITE_SITE_URL?.trim();

  if (configuredOrigin) {
    return configuredOrigin.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }

  return '';
}

export function buildAbsoluteUrl(pathname: string, origin = getSiteOrigin()): string {
  if (/^https?:\/\//i.test(pathname)) {
    return pathname;
  }

  if (!origin) {
    return pathname;
  }

  const normalizedOrigin = origin.replace(/\/+$/, '');
  const normalizedPath = pathname.startsWith('/') ? pathname.slice(1) : pathname;

  return new URL(normalizedPath || '.', `${normalizedOrigin}/`).toString();
}

export function serializeJsonLd(value: JsonLdValue): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

export function buildBreadcrumbJsonLd(
  items: Array<{ label: string; to?: string }>,
  pathname: string,
  origin = getSiteOrigin(),
): JsonLdValue | null {
  if (items.length === 0) {
    return null;
  }

  const currentUrl = buildAbsoluteUrl(pathname || '/', origin);

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.to ? buildAbsoluteUrl(item.to, origin) : currentUrl,
    })),
  };
}

export function buildBrandJsonLd(pathname = '/', origin = getSiteOrigin()): JsonLdValue {
  const websiteUrl = buildAbsoluteUrl(pathname, origin);

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SEO_SITE_NAME,
      url: websiteUrl,
      description: SEO_DEFAULT_DESCRIPTION,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SEO_SITE_NAME,
      url: websiteUrl,
      logo: buildAbsoluteUrl('/logos/logos_verde_confirma.webp', origin),
    },
  ];
}
